'use client'

import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/utils/utils'

import * as React from 'react'

import { XIcon } from 'lucide-react'
import { Dialog as DialogPrimitive } from 'radix-ui'

function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        'fixed inset-0 z-50 bg-black/50 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0',
        className
      )}
      {...props}
    />
  )
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  preventCloseOnOutside = false,
  onInteractOutside,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean
  /**
   * Block dismissal by clicking the backdrop (Escape and the close button still
   * work). Use on form dialogs where an accidental outside-click would discard
   * unsaved input
   */
  preventCloseOnOutside?: boolean
}) {
  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          'fixed top-[50%] left-[50%] z-50 flex w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] flex-col overflow-hidden rounded-lg border bg-card p-0 shadow-card duration-200 outline-none data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 sm:max-w-lg',
          className
        )}
        onInteractOutside={(event) => {
          onInteractOutside?.(event)
          if (preventCloseOnOutside) event.preventDefault()
        }}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            className="absolute top-4 right-4 rounded-xs opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn(
        // When a dialog has no body — header is directly followed by the footer
        // (confirm-style) — drop the header's bottom border so the footer's top
        // border is the single edge-to-edge divider. The :has() variant
        // out-specifies `border-b`.
        'flex flex-col gap-2 border-b border-border-strong bg-muted/50 px-6 pt-6 pb-4 text-center [&:has(+[data-slot=dialog-footer])]:border-b-0 sm:text-left',
        className
      )}
      {...props}
    />
  )
}

// The scrollable region between header and footer. Pass `scrollable` for a
// long body that should scroll between a pinned header and footer: it renders
// a shadcn ScrollArea (styled rail, reveals on hover/scroll) instead of the
// plain div. Requires the DialogContent to cap its height (e.g. `max-h-[85vh]`)
// so the body — `flex-1` inside the flex column — has a bound to scroll within.
function DialogBody({
  className,
  scrollable = false,
  children,
  ...props
}: React.ComponentProps<'div'> & { scrollable?: boolean }) {
  if (scrollable) {
    return (
      // Two Radix ScrollArea quirks handled here:
      // 1. WIDTH — the viewport wraps children in an inline-styled
      //    `display: table; min-width: 100%` div, which sizes to the widest
      //    unwrappable content and lets long text push the body past the
      //    dialog's edge (descendant truncate/min-w-0 can't engage under a
      //    table ancestor). Forced back to block.
      // 2. HEIGHT — the viewport's own `height: 100%` can't resolve against
      //    this flex-derived Root height (treated as auto), so the viewport
      //    grows to its content and paints over the footer instead of
      //    scrolling. Root becomes a flex column and the viewport takes
      //    `min-h-0 flex-1` (a real flexed height) via `viewportClassName`.
      <ScrollArea
        data-slot="dialog-body"
        className="flex min-h-0 flex-1 flex-col [&_[data-slot=scroll-area-viewport]>div]:!block"
        viewportClassName="min-h-0 flex-1"
      >
        <div
          className={cn('flex flex-col gap-4 px-6 py-4', className)}
          {...props}
        >
          {children}
        </div>
      </ScrollArea>
    )
  }

  return (
    <div
      data-slot="dialog-body"
      className={cn('flex flex-col gap-4 px-6 py-4', className)}
      {...props}
    >
      {children}
    </div>
  )
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  showCloseButton?: boolean
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        'flex flex-col-reverse gap-2 border-t border-border-strong bg-muted/50 px-6 py-4 sm:flex-row sm:justify-end',
        className
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close asChild>
          <Button variant="outline">Close</Button>
        </DialogPrimitive.Close>
      )}
    </div>
  )
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('text-lg leading-none font-semibold', className)}
      {...props}
    />
  )
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger
}
